import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { loadTrayPayload } from '@/js/core/utils/tray-prefetch';
import { getTrayComments } from '@/js/core/utils/trays-api';

/**
 * Hydrated from server on /trays/:traySlug routes to avoid redundant API requests.
 * Tracks the UUID to detect stale data on SPA navigation.
 */
export const useTrayViewStore = defineStore('trayView', () => {
  const tray = ref(null);
  const comments = ref([]);
  const commentsMeta = ref(null);
  const isLoadingMore = ref(false);
  const error = ref('');
  const code = ref('');

  const hasMoreComments = computed(() => {
    const meta = commentsMeta.value;
    return meta !== null && meta.current_page < meta.last_page;
  });

  /**
   * Check if we have valid data for a specific UUID.
   * Prevents stale data when navigating between different trays.
   */
  const hasDataForUuid = uuid => {
    return tray.value !== null && tray.value.uuid === uuid;
  };

  /**
   * Set data from server-side hydration
   */
  const setFromHydration = payload => {
    if (payload.error) {
      error.value = payload.error.message || 'Failed to load tray';
      code.value = payload.error.code || '';
      tray.value = null;
      comments.value = [];
      commentsMeta.value = null;
    } else {
      tray.value = payload.tray;
      comments.value = payload.comments?.data || [];
      commentsMeta.value = payload.comments?.meta?.pagination || null;
      error.value = '';
      code.value = '';
    }
  };

  /**
   * Set data from API response
   */
  const setFromApi = (trayData, commentsData, meta) => {
    tray.value = trayData;
    comments.value = commentsData;
    commentsMeta.value = meta;
    error.value = '';
    code.value = '';
  };

  /**
   * Load the tray unless it is already here, or already on its way from the server.
   *
   * Owns the whole "do we have it, where does it come from, where does it go" question
   * so the route guard and the view composable cannot answer it differently.
   *
   * @param {string|null} uuid - The tray UUID
   */
  const ensureTray = async uuid => {
    if (!uuid || hasDataForUuid(uuid)) return;

    // Hydration runs on mount, after the route guard, so the payload has to be read
    // directly here: re-reading a tray the server already sent wastes the request it saved.
    if (window.gsdNowAppData?.trayView?.tray?.uuid === uuid) return;

    const payload = await loadTrayPayload(uuid);

    setFromApi(payload.tray, payload.comments, payload.meta);
  };

  /**
   * Append the next page of comments.
   *
   * Comments are DESC-ordered and addComment() unshifts new ones, so offsets can
   * shift between requests; deduping by uuid keeps overlapping pages harmless.
   */
  const loadMoreComments = async uuid => {
    if (!uuid || isLoadingMore.value || !hasMoreComments.value) return;

    isLoadingMore.value = true;

    try {
      const response = await getTrayComments(uuid, {
        page: commentsMeta.value.current_page + 1,
      });

      const seen = new Set(comments.value.map(c => c.uuid));
      const fresh = (response.data || []).filter(c => !seen.has(c.uuid));

      comments.value = [...comments.value, ...fresh];
      commentsMeta.value = response.meta?.pagination || commentsMeta.value;
    } catch {
      // Meta is untouched on failure, so the button stays visible as the retry path.
    } finally {
      isLoadingMore.value = false;
    }
  };

  /**
   * Set error state
   */
  const setError = (errorMessage, errorCode = '') => {
    error.value = errorMessage;
    code.value = errorCode;
    tray.value = null;
    comments.value = [];
    commentsMeta.value = null;
  };

  /**
   * Reset the store (useful when navigating away)
   */
  const reset = () => {
    tray.value = null;
    comments.value = [];
    commentsMeta.value = null;
    isLoadingMore.value = false;
    error.value = '';
    code.value = '';
  };

  /**
   * Add a new comment to the list (at the beginning since comments are ordered DESC)
   */
  const addComment = comment => {
    comments.value.unshift(comment);
    if (commentsMeta.value) {
      commentsMeta.value.total++;
    }
  };

  /**
   * Update an existing comment
   */
  const updateComment = (commentUuid, updatedData) => {
    const index = comments.value.findIndex(c => c.uuid === commentUuid);
    if (index !== -1) {
      comments.value[index] = { ...comments.value[index], ...updatedData };
    }
  };

  /**
   * Update the current tray's data (after an edit)
   */
  const updateTray = updatedData => {
    if (tray.value) {
      tray.value = { ...tray.value, ...updatedData };
    }
  };

  /**
   * Remove a comment from the list
   */
  const deleteComment = commentUuid => {
    comments.value = comments.value.filter(c => c.uuid !== commentUuid);
    if (commentsMeta.value) {
      commentsMeta.value.total--;
    }
  };

  return {
    tray,
    comments,
    commentsMeta,
    hasMoreComments,
    isLoadingMore,
    error,
    code,
    hasDataForUuid,
    ensureTray,
    loadMoreComments,
    setFromHydration,
    setFromApi,
    setError,
    reset,
    updateTray,
    addComment,
    updateComment,
    deleteComment,
  };
});
